<template>
    <div class="sp-signup">
        <div class="sp-signup__shell">

            <header class="sp-signup__topbar">
                <a class="sp-signup__brand" href="/">
                    <span class="sp-signup__logo"></span>
                </a>
                <nav class="sp-signup__top-links">
                    <a v-if="fromMarketingSite" href="https://switchpitch.com/">Back to site</a>
                    <router-link to="/login">Log in</router-link>
                </nav>
            </header>

            <template v-if="status === 'new'">
                <div class="sp-signup__main">

                    <section class="sp-signup__intro">
                        <div class="sp-signup__eyebrow">
                            <span class="sp-signup__eyebrow-dot"></span>
                            Get started with SwitchPitch
                        </div>
                        <h1 class="sp-signup__headline">Set up your access<br>to SwitchPitch</h1>
                        <p class="sp-signup__sub">Select the account type that matches how you'll use the platform.</p>
                        <div class="sp-signup__benefits">
                            <div class="sp-signup__benefit-item">
                                <span class="sp-signup__check">✓</span>
                                <span>Enterprises can submit needs and get matched to relevant startups, partners, and technologies.</span>
                            </div>
                            <div class="sp-signup__benefit-item">
                                <span class="sp-signup__check">✓</span>
                                <span>Ecosystems can recommend portfolio companies into real enterprise demand.</span>
                            </div>
                            <div class="sp-signup__benefit-item">
                                <span class="sp-signup__check">✓</span>
                                <span>Startups can claim profiles, add proof points, and access needs through participating partners.</span>
                            </div>
                        </div>
                    </section>

                    <section class="sp-signup__card" aria-label="Sign up">

                        <h2>What are you?</h2>
                        <p>Select the account type that matches how you'll use the platform.</p>

                        <div class="sp-signup__role-grid" role="radiogroup" aria-label="Account type">
                            <div
                                class="sp-signup__role-card"
                                :class="{ 'is-active': accountType === 'enterprise' }"
                                role="radio"
                                :aria-checked="accountType === 'enterprise' ? 'true' : 'false'"
                                tabindex="0"
                                @click="accountType = 'enterprise'"
                                @keydown.enter.prevent="accountType = 'enterprise'"
                                @keydown.space.prevent="accountType = 'enterprise'"
                            >
                                <div class="sp-signup__role-icon sp-signup__role-icon--enterprise">E</div>
                                <strong class="sp-signup__role-name">Enterprise</strong>
                                <span class="sp-signup__role-desc">Submit needs, review matches, and manage external partnerships.</span>
                            </div>

                            <div
                                class="sp-signup__role-card"
                                :class="{ 'is-active': accountType === 'ecosystem' }"
                                role="radio"
                                :aria-checked="accountType === 'ecosystem' ? 'true' : 'false'"
                                tabindex="0"
                                @click="accountType = 'ecosystem'"
                                @keydown.enter.prevent="accountType = 'ecosystem'"
                                @keydown.space.prevent="accountType = 'ecosystem'"
                            >
                                <div class="sp-signup__role-icon sp-signup__role-icon--ecosystem">N</div>
                                <strong class="sp-signup__role-name">Ecosystem</strong>
                                <span class="sp-signup__role-desc">Recommend startups from your VC, accelerator, or university network.</span>
                            </div>

                            <div
                                class="sp-signup__role-card"
                                :class="{ 'is-active': accountType === 'startup' }"
                                role="radio"
                                :aria-checked="accountType === 'startup' ? 'true' : 'false'"
                                tabindex="0"
                                @click="accountType = 'startup'"
                                @keydown.enter.prevent="accountType = 'startup'"
                                @keydown.space.prevent="accountType = 'startup'"
                            >
                                <div class="sp-signup__role-icon sp-signup__role-icon--startup">S</div>
                                <strong class="sp-signup__role-name">Startup</strong>
                                <span class="sp-signup__role-desc">Claim your profile and request warm intros through partners.</span>
                            </div>
                        </div>

                        <div class="sp-signup__benefit-badge">
                            <div class="sp-signup__badge-spark">✦</div>
                            <div>
                                <strong class="sp-signup__badge-title">{{ roleBenefit.title }}</strong>
                                <span class="sp-signup__badge-copy">{{ roleBenefit.copy }}</span>
                            </div>
                        </div>

                        <div class="sp-signup__signup-box">
                            <div class="sp-signup__form-header">
                                <h3>Create your account</h3>
                                <router-link to="/login">Already have an account?</router-link>
                            </div>
                            <Signup
                                :company-type="accountType"
                                :fixed-company-type="true"
                                v-on:setAccountType="setAccountType"
                                v-on:registered="registered"
                            />
                        </div>

                        <p class="sp-signup__handoff">After signup, you'll enter the SwitchPitch platform workspace.</p>
                    </section>

                </div>
            </template>

            <div v-if="status === 'registered'" class="sp-signup__confirm">
                <div class="sp-signup__confirm-box">
                    <h1>Confirm Email</h1>
                    <p>We sent an email to {{ email }} to activate your account. Click the link to complete registration.</p>
                    <p><strong>If you don't receive the email, check your spam folder.</strong></p>
                    <div class="text-center">
                        <button class="button" type="button" @click="$router.push('/login')">Log In</button>
                    </div>
                </div>
            </div>

            <div v-if="status === 'registered-confirmed'" class="sp-signup__confirm">
                <div class="sp-signup__confirm-box">
                    <h1>Account Confirmed</h1>
                    <p>Account has been successfully confirmed.</p>
                    <p>Please login:</p>
                    <div class="text-center">
                        <button class="button" type="button" @click="$router.push('/login')">Log In</button>
                    </div>
                </div>
            </div>

            <footer class="sp-signup__footer">
                <div>© SwitchPitch</div>
                <div>External partnerships, enterprise needs, and trusted ecosystem recommendations.</div>
            </footer>

        </div>
    </div>
</template>

<script>

import Signup from "@/components/common/Signup";

const ROLE_BENEFIT = {
    enterprise: {
        title: 'Enterprise Explorer accounts are free to start.',
        copy: 'Submit needs, invite users, and receive matched recommendations. Full platform access adds workflows, reporting, market intelligence, and analyst support.',
    },
    ecosystem: {
        title: 'Ecosystem partners can join with low friction.',
        copy: 'View relevant enterprise needs, recommend companies, and let startups request warm introductions through your organization.',
    },
    startup: {
        title: 'Startups can claim and enrich their profiles.',
        copy: 'Add decks, demo links, client lists, and proof points. Access to enterprise needs is available through participating VC, accelerator, and university partners.',
    },
};

export default {
    components: {
        Signup,
    },

    data() {
        return {
            accountType: "enterprise",
            status: "new",
            email: "",
            fromMarketingSite: false,
        }
    },

    computed: {
        roleBenefit() {
            return ROLE_BENEFIT[this.accountType] || ROLE_BENEFIT.enterprise;
        },
    },

    mounted() {
        document.body.classList.add('sp-signup-active')

        if (this.$route.query.account_type) {
            this.accountType = this.$route.query.account_type
        }

        try {
            const referrer = new URL(document.referrer);
            this.fromMarketingSite = referrer.hostname === 'switchpitch.com' || referrer.hostname === 'www.switchpitch.com';
        } catch {
            // document.referrer is empty or not a valid URL — user navigated directly
        }
    },

    beforeDestroy() {
        document.body.classList.remove('sp-signup-active')
    },

    methods: {
        setAccountType(accountType) {
            this.accountType = accountType
        },

        registered({status, email}) {
            this.status = status
            this.email = email
        }
    }
}
</script>
